import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@/lib/use-server-fn";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";

import { submitContact, submitParticipation } from "@/lib/public-data.functions";
import { useSiteSettings } from "@/lib/use-site-settings";
import { PageHero, Section } from "@/components/site/bits";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/contato")({
  head: () => ({
    meta: [
      { title: "Contato | Professor Rurdiney — 40123" },
      {
        name: "description",
        content: "Fale com a equipe do Professor Rurdiney ou cadastre-se para participar da campanha nº 40123.",
      },
      { property: "og:title", content: "Contato | Professor Rurdiney — 40123" },
      {
        property: "og:description",
        content: "Entre em contato ou quero participar da campanha do Professor Rurdiney.",
      },
    ],
  }),
  component: ContatoPage,
});

const contactSchema = z.object({
  name: z.string().min(2, "Informe seu nome completo"),
  email: z.string().email("E-mail inválido").optional().or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
  city: z.string().optional().or(z.literal("")),
  subject: z.string().optional().or(z.literal("")),
  message: z.string().min(5, "Escreva uma mensagem"),
  consent: z.boolean().refine((v) => v === true, {
    message: "É necessário aceitar a política de privacidade",
  }),
});
type ContactValues = z.infer<typeof contactSchema>;

const participationSchema = z.object({
  name: z.string().min(2, "Informe seu nome completo"),
  email: z.string().email("E-mail inválido").optional().or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
  city: z.string().optional().or(z.literal("")),
  neighborhood: z.string().optional().or(z.literal("")),
  interest: z.string().min(2, "Selecione uma opção"),
  message: z.string().optional().or(z.literal("")),
  consent: z.boolean().refine((v) => v === true, {
    message: "É necessário aceitar a política de privacidade",
  }),
});
type ParticipationValues = z.infer<typeof participationSchema>;

const INTERESTS = ["Voluntariado", "Doação de tempo", "Divulgação", "Apoio no bairro", "Outro"];

function ConsentLabel() {
  return (
    <span>
      Li e concordo com a{" "}
      <Link to="/privacidade" className="font-semibold text-primary underline underline-offset-2">
        política de privacidade
      </Link>{" "}
      (LGPD).
    </span>
  );
}

function ContactForm() {
  const submitFn = useServerFn(submitContact);
  const form = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      city: "",
      subject: "",
      message: "",
      consent: false,
    },
  });

  const mutation = useMutation({
    mutationFn: (values: ContactValues) => submitFn({ data: values }),
    onSuccess: () => {
      toast.success("Mensagem enviada com sucesso! Em breve entraremos em contato.");
      form.reset();
    },
    onError: () => {
      toast.error("Não foi possível enviar sua mensagem. Tente novamente.");
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
        className="space-y-5"
      >
        <div className="grid gap-5 md:grid-cols-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome completo</FormLabel>
                <FormControl>
                  <Input placeholder="Seu nome" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>E-mail</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="voce@email.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone</FormLabel>
                <FormControl>
                  <Input placeholder="(98) 99999-9999" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cidade</FormLabel>
                <FormControl>
                  <Input placeholder="São Luís" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Assunto</FormLabel>
              <FormControl>
                <Input placeholder="Sobre o que você quer falar?" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mensagem</FormLabel>
              <FormControl>
                <Textarea rows={5} placeholder="Escreva sua mensagem..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="consent"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start gap-3 rounded-xl border border-border bg-secondary/40 p-4">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <div>
                <FormLabel className="font-normal leading-relaxed">
                  <ConsentLabel />
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={mutation.isPending}
          className="w-full rounded-xl bg-primary py-6 text-sm font-bold uppercase tracking-wide text-primary-foreground hover:bg-primary-deep md:w-auto"
        >
          {mutation.isPending ? "Enviando..." : "Enviar mensagem"}
        </Button>
      </form>
    </Form>
  );
}

function ParticipationForm() {
  const submitFn = useServerFn(submitParticipation);
  const form = useForm<ParticipationValues>({
    resolver: zodResolver(participationSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      city: "",
      neighborhood: "",
      interest: "",
      message: "",
      consent: false,
    },
  });

  const mutation = useMutation({
    mutationFn: (values: ParticipationValues) => submitFn({ data: values }),
    onSuccess: () => {
      toast.success("Cadastro recebido! Obrigado por querer participar da nossa campanha.");
      form.reset();
    },
    onError: () => {
      toast.error("Não foi possível enviar seu cadastro. Tente novamente.");
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
        className="space-y-5"
      >
        <div className="grid gap-5 md:grid-cols-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome completo</FormLabel>
                <FormControl>
                  <Input placeholder="Seu nome" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>E-mail</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="voce@email.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone</FormLabel>
                <FormControl>
                  <Input placeholder="(98) 99999-9999" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cidade</FormLabel>
                <FormControl>
                  <Input placeholder="São Luís" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="neighborhood"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bairro</FormLabel>
                <FormControl>
                  <Input placeholder="Seu bairro" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="interest"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Como você quer ajudar?</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma opção" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {INTERESTS.map((i) => (
                    <SelectItem key={i} value={i}>
                      {i}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mensagem (opcional)</FormLabel>
              <FormControl>
                <Textarea rows={4} placeholder="Conte um pouco mais..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="consent"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start gap-3 rounded-xl border border-border bg-secondary/40 p-4">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <div>
                <FormLabel className="font-normal leading-relaxed">
                  <ConsentLabel />
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={mutation.isPending}
          className="w-full rounded-xl bg-accent py-6 text-sm font-bold uppercase tracking-wide text-accent-foreground hover:brightness-95 md:w-auto"
        >
          {mutation.isPending ? "Enviando..." : "Quero participar"}
        </Button>
      </form>
    </Form>
  );
}

function ContatoPage() {
  const settings = useSiteSettings();
  const whatsappLink = settings.whatsapp
    ? `https://wa.me/${settings.whatsapp.replace(/\D/g, "")}`
    : "";

  return (
    <>
      <PageHero
        eyebrow="Fale com a gente"
        title="Contato"
        description="Tem uma dúvida, sugestão ou quer participar da campanha? Escolha uma opção abaixo."
      />

      <Section>
        <div className="grid gap-10 md:grid-cols-[1fr_1.6fr]">
          <aside className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <h3 className="font-display text-lg text-foreground">Informações de contato</h3>
              <div className="mt-4 space-y-3 text-sm text-muted-foreground">
                {settings.email && (
                  <p className="flex items-center gap-2">
                    <Mail className="size-4 text-primary" /> {settings.email}
                  </p>
                )}
                {settings.phone && (
                  <p className="flex items-center gap-2">
                    <Phone className="size-4 text-primary" /> {settings.phone}
                  </p>
                )}
                {settings.address && (
                  <p className="flex items-center gap-2">
                    <MapPin className="size-4 text-primary" /> {settings.address}
                  </p>
                )}
              </div>
              {whatsappLink && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-sm font-bold uppercase tracking-wide text-accent-foreground shadow-accent transition-transform hover:scale-[1.02]"
                >
                  <MessageCircle className="size-4" /> Falar no WhatsApp
                </a>
              )}
            </div>
          </aside>

          <div className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-8">
            <Tabs defaultValue="contato">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="contato">Fale conosco</TabsTrigger>
                <TabsTrigger value="participar">Quero participar</TabsTrigger>
              </TabsList>
              <TabsContent value="contato" className="mt-6">
                <ContactForm />
              </TabsContent>
              <TabsContent value="participar" className="mt-6">
                <ParticipationForm />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </Section>
    </>
  );
}
